import { memo, useCallback, useEffect, useRef, useState } from 'react';

import type { SearchResult } from './utils';
import { truncate } from './utils';

interface SearchPanelProps {
	searchResults: SearchResult[];
	onQueryChange: (query: string) => void;
	onNavigate: (path: string) => void;
}

export const SearchPanel = memo(function SearchPanel({
	searchResults,
	onQueryChange,
	onNavigate,
}: SearchPanelProps) {
	const [inputValue, setInputValue] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setInputValue(val);
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				onQueryChange(val);
				setIsOpen(val.length > 0);
			}, 300);
		},
		[onQueryChange],
	);

	const handleResultClick = useCallback(
		(path: string) => {
			onNavigate(path);
			setIsOpen(false);
		},
		[onNavigate],
	);

	const handleFocus = useCallback(() => {
		if (inputValue.length > 0 && searchResults.length > 0) {
			setIsOpen(true);
		}
	}, [inputValue, searchResults]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			setIsOpen(false);
			(e.target as HTMLInputElement).blur();
		}
	}, []);

	return (
		<div ref={containerRef} className="relative">
			<input
				type="text"
				placeholder="Search…"
				className="w-32 min-w-0 rounded border border-gray-300 bg-white px-2
					py-1 font-mono text-xs text-gray-800 placeholder-gray-400 outline-none
					focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
				value={inputValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onKeyDown={handleKeyDown}
			/>

			{isOpen && searchResults.length > 0 && (
				<div
					className="absolute top-full left-0 z-50 mt-1 max-h-72 w-96
						overflow-y-auto rounded-lg border border-gray-200 bg-white
						shadow-lg"
				>
					<div className="px-2 py-1 text-xs text-gray-400">
						{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
					</div>
					{searchResults.slice(0, 100).map((result) => (
						<button
							key={`${result.path}-${result.matchType}`}
							className="flex w-full flex-col gap-0.5 px-2 py-1.5 text-left
								hover:bg-gray-50"
							onClick={() => handleResultClick(result.path)}
						>
							<span className="font-mono text-xs text-gray-500">
								{result.path}
							</span>
							<span className="text-xs">
								<span className="text-gray-400">
									{result.matchType === 'key' ? 'key: ' : 'value: '}
								</span>
								<span className="text-gray-700">
									{truncate(result.preview, 80)}
								</span>
							</span>
						</button>
					))}
					{searchResults.length > 100 && (
						<div className="px-2 py-1 text-xs text-gray-400">
							…and {searchResults.length - 100} more
						</div>
					)}
				</div>
			)}

			{isOpen && inputValue.length > 0 && searchResults.length === 0 && (
				<div
					className="absolute top-full left-0 z-50 mt-1 w-64 rounded-lg border
						border-gray-200 bg-white px-3 py-2 text-xs text-gray-400 shadow-lg"
				>
					No results
				</div>
			)}
		</div>
	);
});
